import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// WeChat-style "What's New" popup: shows ONCE PER ACCOUNT (server-side flag) — plus a device flag as a fallback.
export const ReleaseNotesModal = ({ lang = "zh" }) => {
  const [note, setNote] = useState(null);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const isBoss = ["admin", "owner"].includes(user?.role);

  useEffect(() => {
    axios.get(`${API}/release-notes/latest`).then(r => {
      const n = r.data;
      if (n && n.version && (n.items || []).length > 0 && !n.seen && localStorage.getItem("seen_release_note") !== n.version) {
        setNote(n);
        setOpen(true);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    if (note) {
      localStorage.setItem("seen_release_note", note.version);
      axios.post(`${API}/release-notes/seen`, { version: note.version }).catch(() => {});
    }
    setOpen(false);
  };

  const applyLabor = async () => {
    try {
      const r = await axios.post(`${API}/release-notes/apply-labor`, { version: note.version });
      toast.success(lang === "zh" ? `已把最低工资更新为 Bs ${r.data.lottt_salario_minimo_bs}` : `Salario mínimo actualizado a Bs ${r.data.lottt_salario_minimo_bs}`);
      localStorage.setItem("seen_release_note", note.version); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!note) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="release-notes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {note.kind === "fiscal_alert"
              ? <>⚖️ {lang === "zh" ? "税务法规提醒 (SENIAT)" : "Alerta fiscal SENIAT"}</>
              : note.kind === "labor_alert"
              ? <>📢 {lang === "zh" ? "劳动法规 / 最低工资变动" : "Aviso laboral · salario mínimo"}</>
              : <>✨ {lang === "zh" ? "新功能上线" : "Novedades"} · <span className="text-emerald-400 font-mono text-sm">{note.version}</span></>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {note.items.map((it, i) => (
            <div key={i} className="flex gap-2 text-sm bg-slate-900/50 rounded-lg px-3 py-2">
              <span className="text-emerald-400 shrink-0">•</span>
              <span className="text-slate-200">{lang === "zh" ? (it.zh || it.es) : (it.es || it.zh)}</span>
            </div>
          ))}
        </div>
        {note.kind === "labor_alert" && (note.min_salary_bs > 0 || note.cestaticket_bs > 0) && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-2" data-testid="labor-alert-box">
            {note.min_salary_bs > 0 && <p className="text-amber-200">{lang === "zh" ? "新法定最低工资" : "Nuevo salario mínimo"}: <b>Bs {Number(note.min_salary_bs).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</b>{note.effective_date ? ` · ${lang === "zh" ? "生效" : "desde"} ${note.effective_date}` : ""}</p>}
            {note.cestaticket_bs > 0 && <p className="text-amber-200">{lang === "zh" ? "新 Cestaticket" : "Nuevo Cestaticket"}: <b>Bs {Number(note.cestaticket_bs).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</b></p>}
            {note.source_url && <a href={note.source_url} target="_blank" rel="noreferrer" className="text-xs underline text-amber-300">{lang === "zh" ? "查看官方公报" : "Ver Gaceta / fuente oficial"}</a>}
            {note.min_salary_bs > 0 && isBoss && (
              <Button onClick={applyLabor} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="labor-alert-apply">
                {lang === "zh" ? "一键应用到我的工资单（最低工资）" : "Aplicar a mis recibos de pago (salario mínimo)"}
              </Button>
            )}
            <p className="text-[11px] text-slate-300">{lang === "zh" ? "记得同时检查「员工法务 → 档案」里每个员工的基本工资是否不低于新最低工资。" : "Revisa también en RRHH Legal → Expediente que ningún salario básico quede por debajo del nuevo mínimo."}</p>
          </div>
        )}
        <Button onClick={dismiss} className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="release-notes-ok">
          {lang === "zh" ? "知道了 👌" : "¡Entendido! 👌"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
